import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
	type Activity,
	CheckCircle,
	Database,
	Mail,
	Receipt,
	RefreshCw,
	XCircle,
} from "lucide-react";

// ============================================================
// Health Page — System-Status + Diagnostics
// ============================================================

interface ServiceStatus {
	lastSync: string | null;
	totalProcessed: number;
}

interface HealthResponse {
	status: string;
	diagnostics: {
		timestamp: string;
		version: string;
		database: {
			connected: boolean;
			poolStats: {
				totalConnections: number;
				idleConnections: number;
				waitingCount: number;
			} | null;
			tables: Record<string, number>;
		};
		services?: {
			email?: ServiceStatus;
			lexoffice?: ServiceStatus;
		};
	};
}

export function HealthPage() {
	const query = useQuery({
		queryKey: ["health"],
		queryFn: () => api.get<HealthResponse>("/health/full"),
		refetchInterval: 30000, // Alle 30 Sekunden
	});

	const health = query.data;
	const isHealthy = health?.status === "healthy";
	const services = health?.diagnostics?.services;

	return (
		<div className="space-y-6 animate-fade-in">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-display text-2xl">System-Status</h1>
					<p className="text-sm text-stone-500">
						{health
							? `v${health.diagnostics.version} · ${new Date(health.diagnostics.timestamp).toLocaleString("de-DE")}`
							: "Laden..."}
					</p>
				</div>
				<button
					type="button"
					onClick={() => query.refetch()}
					className="btn-ghost"
				>
					<RefreshCw
						size={14}
						className={cn(query.isFetching && "animate-spin")}
					/>{" "}
					Aktualisieren
				</button>
			</div>

			{/* Overall Status */}
			<div
				className={cn(
					"card flex items-center gap-3",
					isHealthy
						? "border-green-200 bg-green-50/50"
						: "border-red-200 bg-red-50/50",
				)}
			>
				{isHealthy ? (
					<CheckCircle size={24} className="text-green-600" />
				) : (
					<XCircle size={24} className="text-red-600" />
				)}
				<div>
					<p className="font-medium">
						{isHealthy ? "Datenbank verbunden" : "Probleme erkannt"}
					</p>
					<p className="text-sm text-stone-500">
						{health?.diagnostics.timestamp
							? `Letzter Check: ${new Date(health.diagnostics.timestamp).toLocaleTimeString("de-DE")}`
							: ""}
					</p>
				</div>
			</div>

			{/* Service Cards */}
			{health && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{/* Database */}
					<ServiceCard
						icon={Database}
						title="Datenbank"
						status={health.diagnostics.database.connected}
						details={[
							...(health.diagnostics.database.poolStats
								? [
										`Verbindungen: ${health.diagnostics.database.poolStats.totalConnections}`,
										`Idle: ${health.diagnostics.database.poolStats.idleConnections}`,
										`Wartend: ${health.diagnostics.database.poolStats.waitingCount}`,
									]
								: []),
							...Object.entries(health.diagnostics.database.tables).map(
								([table, count]) => `${table}: ${count} Einträge`,
							),
						]}
					/>

					{/* Email Sync */}
					<SyncServiceCard
						icon={Mail}
						title="E-Mail-Sync"
						service={services?.email}
						unit="E-Mails"
					/>

					{/* LexOffice Sync */}
					<SyncServiceCard
						icon={Receipt}
						title="LexOffice-Sync"
						service={services?.lexoffice}
						unit="Belege"
					/>
				</div>
			)}

			{/* Loading State */}
			{query.isLoading && (
				<div className="flex justify-center py-12">
					<div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
				</div>
			)}
		</div>
	);
}

// ============================================================
// Service Card
// ============================================================

function ServiceCard({
	icon: Icon,
	title,
	status,
	details,
}: {
	icon: typeof Activity;
	title: string;
	status: boolean;
	details: string[];
}) {
	return (
		<div className="card">
			<div className="flex items-center gap-3 mb-3">
				<div
					className={cn(
						"h-8 w-8 rounded-lg flex items-center justify-center",
						status ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600",
					)}
				>
					<Icon size={16} />
				</div>
				<div className="flex-1">
					<p className="font-medium text-stone-900">{title}</p>
				</div>
				<StatusDot ok={status} />
			</div>
			<div className="space-y-1">
				{details.map((d) => (
					<p key={d} className="text-xs text-stone-500">
						{d}
					</p>
				))}
			</div>
		</div>
	);
}

// ============================================================
// Sync Service Card — uses actual sync timestamps
// ============================================================

function SyncServiceCard({
	icon: Icon,
	title,
	service,
	unit,
}: {
	icon: typeof Activity;
	title: string;
	service?: ServiceStatus;
	unit: string;
}) {
	const hasData = (service?.totalProcessed ?? 0) > 0;
	const hasSynced = !!service?.lastSync;
	const lastSync = service?.lastSync
		? new Date(service.lastSync).toLocaleString("de-DE")
		: "Nie";

	return (
		<div className="card">
			<div className="flex items-center gap-3 mb-3">
				<div
					className={cn(
						"h-8 w-8 rounded-lg flex items-center justify-center",
						hasSynced
							? "bg-green-100 text-green-600"
							: hasData
								? "bg-amber-100 text-amber-600"
								: "bg-stone-100 text-stone-400",
					)}
				>
					<Icon size={16} />
				</div>
				<div className="flex-1">
					<p className="font-medium text-stone-900">{title}</p>
				</div>
				<StatusDot ok={hasSynced} />
			</div>
			<div className="space-y-1">
				<p className="text-xs text-stone-500">
					Letzter Sync: {lastSync}
				</p>
				<p className="text-xs text-stone-500">
					{unit}: {service?.totalProcessed ?? 0}
				</p>
				{!hasSynced && hasData && (
					<p className="text-xs text-amber-600">
						Daten vorhanden, aber kein Sync-Zeitstempel
					</p>
				)}
			</div>
		</div>
	);
}

function StatusDot({ ok }: { ok: boolean }) {
	return (
		<span
			className={cn(
				"h-2.5 w-2.5 rounded-full",
				ok ? "bg-green-500" : "bg-stone-300",
			)}
		/>
	);
}
